import { useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { OFFER, offerActive, discounted, eur } from "@/lib/offer";

const TYPES = [
  { id: "ristorante", label: "Ristorante / Pizzeria", base: 999 },
  { id: "hotel", label: "Hotel / Resort", base: 1890 },
  { id: "bb", label: "B&B / Domus", base: 999 },
  { id: "bottega", label: "Bottega / Negozio", base: 999 },
];

const FEATURES = [
  { id: "booking", label: "Prenotazioni direct", price: 490 },
  { id: "menu", label: "Menu digitale & QR", price: 290 },
  { id: "seo", label: "SEO locale & Maps", price: 350 },
  { id: "multi", label: "Sito multilingua", price: 590 },
];

export default function Estimator() {
  const [type, setType] = useState("ristorante");
  const [selected, setSelected] = useState([]);
  const lenis = useLenis();

  const base = TYPES.find((t) => t.id === type).base;
  const chosen = FEATURES.filter((f) => selected.includes(f.id));
  const total = base + chosen.reduce((s, f) => s + f.price, 0);
  const low = Math.max(999, Math.round((total * 0.92) / 10) * 10);
  const high = Math.round((total * 1.12) / 10) * 10;

  const toggleFeature = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const goContact = (e) => {
    e.preventDefault();
    lenis?.scrollTo("#contatti", { offset: -84, duration: 1.5 });
  };

  return (
    <section id="stima" className="bg-panel py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          tag="Preventivo espresso"
          title={
            <>
              Stima il tuo investimento in <em className="italic text-terracotta">30 secondi.</em>
            </>
          }
          sub="Scegli la tua attività e i servizi che ti servono: il range si aggiorna in tempo reale."
        />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-14 grid grid-cols-1 gap-10 rounded-3xl border border-line bg-panel-2 p-6 sm:p-8 lg:grid-cols-2 lg:p-10"
        >
          <div>
            <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-stone">
              La tua attività
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  data-testid={`estimator-type-${t.id}`}
                  aria-pressed={type === t.id}
                  className={`rounded-xl border px-4 py-3.5 text-left font-accent text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                    type === t.id
                      ? "border-terracotta bg-terracotta/10 text-bone"
                      : "border-line bg-ink text-stone hover:border-line-strong hover:text-bone"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <p className="mt-8 font-accent text-[11px] uppercase tracking-[0.28em] text-stone">
              Servizi aggiuntivi
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {FEATURES.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => toggleFeature(f.id)}
                  data-testid={`estimator-feature-${f.id}`}
                  aria-pressed={selected.includes(f.id)}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs transition-all duration-300 ${
                    selected.includes(f.id)
                      ? "border-terracotta bg-terracotta/10 text-bone"
                      : "border-line bg-ink text-stone hover:border-line-strong hover:text-bone"
                  }`}
                >
                  {selected.includes(f.id) && <Check size={13} className="text-terracotta" />}
                  {f.label}
                  <span className="text-faded">+{eur(f.price)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-2xl border border-line bg-ink p-7 lg:p-9">
            <div>
              <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-terracotta">
                Investimento indicativo
              </p>
              <p
                data-testid="estimator-total"
                className="mt-4 font-serif text-4xl tracking-tight text-bone sm:text-5xl"
              >
                {eur(discounted(low))} <span className="text-faded">—</span> {eur(discounted(high))}
              </p>
              {offerActive() && (
                <p className="mt-2 text-xs text-faded" data-testid="estimator-list-price">
                  Listino <span className="line-through">{eur(low)} — {eur(high)}</span>
                  <span className="ml-2 text-ochre">{OFFER.short} fino al {OFFER.deadlineLabel}</span>
                </p>
              )}
              <p className="mt-2 text-xs text-faded">IVA esclusa · preventivo preciso entro 24 ore</p>

              <div className="mt-7 space-y-2.5 border-t border-line pt-6 text-sm">
                <div className="flex justify-between text-stone">
                  <span>Base {TYPES.find((t) => t.id === type).label}</span>
                  <span className="text-bone">{eur(base)}</span>
                </div>
                {chosen.map((f) => (
                  <div key={f.id} className="flex justify-between text-stone">
                    <span>{f.label}</span>
                    <span className="text-bone">+{eur(f.price)}</span>
                  </div>
                ))}
                {chosen.length === 0 && (
                  <p className="text-xs text-faded">
                    Nessun servizio extra selezionato: aggiungine uno per personalizzare la stima.
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={goContact}
              data-testid="estimator-cta"
              className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-terracotta px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
            >
              Richiedi preventivo preciso
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}